// ===========================================
// Multi GWP Sync (supports multiple independent GWPs)
// Perf + Reliability hardened version
// ===========================================

(() => {
  // --------------------
  // Guard against double-loading
  // --------------------
  if (window.__MULTI_GWP_SYNC_INIT__) return;
  window.__MULTI_GWP_SYNC_INIT__ = true;

  // --------------------
  // CONFIG
  // --------------------
  const DEBUG_GWP = false;
  const log = (...a) => DEBUG_GWP && console.log("[MultiGWP]", ...a);
  const warn = (...a) => DEBUG_GWP && console.warn("[MultiGWP]", ...a);

  /**
   * Rules:
   * - qualifiers: variant IDs that qualify for the GWP
   * - gwpVariantId: the GWP variant to add/sync/remove
   * - qtyMode:
   *    - "sum": GWP qty = sum of all qualifying item quantities
   *    - "bool": GWP qty = 1 if any qualifying item is present (only one per order)
   */
  const GWP_RULES = [
    {
      name: "FireHorseGWP",
      qualifiers: ["56087469850790"], // Hold Your Fire Horses bundle
      gwpVariantId: "56189984080038", // Set of red envelopes
      qtyMode: "sum",
    },
    {
      name: "PastelHookGWP",
      qualifiers: [
        "56169901621414",
        "56169987145894",
        "56168798879910",
        "44807518486694",
        "44696494473382",
        "44680546451622",
        "44680541700262",
      ],
      gwpVariantId: "44811311251622", // Pastel crochet hook
      qtyMode: "bool", // only ONE per order if any qualifier exists
    },
  ];

  // --------------------
  // Tunables (performance + stability)
  // --------------------
  const CART_ENDPOINTS = {
    get: "/cart.js",
    add: "/cart/add.js",
    change: "/cart/change.js",
  };

  // Debounce time for DOM-triggered enforcement
  const ENFORCE_DEBOUNCE_MS = 250;

  // Post-mutation verification delay (gives SlideCart time to rerender)
  const VERIFY_AFTER_CHANGE_MS = 650;

  // Mutation retries
  const MUTATION_TRIES = 3;
  const MUTATION_BACKOFF_MS = 250;

  // Initial “settle” read retries (helps when cart isn’t ready yet)
  const INITIAL_CART_READ_RETRIES = 12;
  const INITIAL_CART_READ_DELAY = 300;

  // Subsequent reads are lighter
  const NORMAL_CART_READ_RETRIES = 4;
  const NORMAL_CART_READ_DELAY = 200;

  // --------------------
  // Shared helpers
  // --------------------
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  async function fetchJson(url, options = {}) {
    const res = await fetch(url, {
      credentials: "same-origin",
      ...options,
    });
    // We still attempt to parse JSON for debugging when possible
    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      // ignore
    }
    return { res, data };
  }

  async function getCart() {
    const { res, data } = await fetchJson(CART_ENDPOINTS.get);
    if (!res.ok) throw new Error(`getCart failed: HTTP ${res.status}`);
    return data;
  }

  async function getCartWithOptionalWait(retries, delay) {
    for (let i = 0; i < retries; i++) {
      try {
        const cart = await getCart();
        if (cart && Array.isArray(cart.items)) return cart;
      } catch (e) {
        // swallow and retry
      }
      await sleep(delay);
    }
    return getCart();
  }

  async function withRetry(fn, tries = MUTATION_TRIES, baseWait = MUTATION_BACKOFF_MS) {
    let lastErr;
    for (let i = 0; i < tries; i++) {
      try {
        const out = await fn();
        // If fn returns fetch Response, require ok
        if (out && typeof out.ok === "boolean") {
          if (out.ok) return out;
          lastErr = new Error(`HTTP ${out.status}`);
        } else {
          // Otherwise assume success
          return out;
        }
      } catch (e) {
        lastErr = e;
      }
      await sleep(baseWait * (i + 1));
    }
    throw lastErr;
  }

  async function addVariant(variantId, quantity) {
    log("Attempting to add variant", { variantId, quantity });

    const res = await fetch(CART_ENDPOINTS.add, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
    });

    if (DEBUG_GWP && !res.ok) {
      let text = "";
      try {
        text = await res.text();
      } catch {}
      warn("FAILED to add variant", { variantId, quantity, status: res.status, text });
    }

    return res;
  }

  async function setLineQuantity(lineKey, quantity) {
    const res = await fetch(CART_ENDPOINTS.change, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: lineKey, quantity }),
    });

    if (DEBUG_GWP && !res.ok) {
      let text = "";
      try {
        text = await res.text();
      } catch {}
      warn("FAILED to change line qty", { lineKey, quantity, status: res.status, text });
    }

    return res;
  }

  // --------------------
  // UI lock (SlideCart + theme cart qty controls)
  // --------------------
  function disableQuantityControls(variantId) {
    const vid = String(variantId);

    // AMP SlideCart item rows (if present)
    document.querySelectorAll(`[data-variant-id="${vid}"]`).forEach((line) => {
      const dec = line.querySelector('button[aria-label="decrease quantity"]');
      const inc = line.querySelector('button[aria-label="increase quantity"]');
      const input = line.querySelector(
        ".quantity-selector input[type='text'], .quantity-selector input[type='number']"
      );

      if (dec) {
        dec.disabled = true;
        dec.setAttribute("aria-disabled", "true");
        dec.classList.add("gwp-locked");
      }
      if (inc) {
        inc.disabled = true;
        inc.setAttribute("aria-disabled", "true");
        inc.classList.add("gwp-locked");
      }
      if (input) {
        input.readOnly = true;
        input.classList.add("gwp-locked");
      }
    });

    // Theme cart inputs (if present)
    document.querySelectorAll(".cart__qty-input").forEach((input) => {
      const match = input.id && input.id.match(/updates_large_(\d+)/);
      if (match && match[1] === vid) {
        input.readOnly = true;
        input.classList.add("gwp-locked");
      }
    });
  }

  // --------------------
  // Rule helpers
  // --------------------
  function getDesiredGwpQty(cart, qualifiers, qtyMode) {
    const qualifierSet = new Set(qualifiers.map(String));

    const qualifyingQty = (cart.items || [])
      .filter((i) => qualifierSet.has(String(i.variant_id)))
      .reduce((sum, i) => sum + (i.quantity || 0), 0);

    if (qtyMode === "bool") return qualifyingQty > 0 ? 1 : 0;
    return qualifyingQty; // "sum"
  }

  function buildVariantMap(cart) {
    const byVariant = new Map();
    for (const item of cart.items || []) {
      const vid = String(item.variant_id);
      if (!byVariant.has(vid)) byVariant.set(vid, item);
    }
    return byVariant;
  }

  // --------------------
  // Enforcement logic (all GWPs)
  // --------------------
  let firstEnforcement = true;
  let enforcing = false;
  let pending = false;

  async function enforceAllGwpRules({ longWait = false, reason = "unknown" } = {}) {
    if (enforcing) {
      pending = true;
      log("Enforcement requested while enforcing; marking pending rerun.", { reason });
      return;
    }

    enforcing = true;
    pending = false;

    try {
      log("Enforcing all GWP rules…", { reason });

      const retries = longWait || firstEnforcement ? INITIAL_CART_READ_RETRIES : NORMAL_CART_READ_RETRIES;
      const delay = longWait || firstEnforcement ? INITIAL_CART_READ_DELAY : NORMAL_CART_READ_DELAY;

      const cart = await getCartWithOptionalWait(retries, delay);
      firstEnforcement = false;

      const byVariant = buildVariantMap(cart);

      let didChange = false;

      // Process rules sequentially (stable, predictable)
      for (const rule of GWP_RULES) {
        const name = rule.name || "GWP";
        const qualifiers = (rule.qualifiers || []).map(String);
        const gwpVariantId = String(rule.gwpVariantId || "");
        const qtyMode = rule.qtyMode || "sum";

        if (!gwpVariantId || !qualifiers.length) {
          warn(`${name}: missing gwpVariantId or qualifiers, skipping`, rule);
          continue;
        }

        const desiredQty = getDesiredGwpQty(cart, qualifiers, qtyMode);
        const gwpItem = byVariant.get(gwpVariantId);

        log(`${name}: desiredQty=${desiredQty}`, { gwpVariantId, qtyMode });

        // If no longer qualifies, remove the gift
        if (desiredQty <= 0) {
          if (gwpItem) {
            log(`${name}: removing GWP (no qualifiers in cart)`);
            await withRetry(() => setLineQuantity(gwpItem.key, 0));
            didChange = true;
          }
          continue;
        }

        // Qualifies: ensure gift exists
        if (!gwpItem) {
          log(`${name}: adding GWP qty=${desiredQty}`);
          await withRetry(() => addVariant(gwpVariantId, desiredQty));
          didChange = true;
          continue;
        }

        // Qualifies: ensure qty matches desired
        if ((gwpItem.quantity || 0) !== desiredQty) {
          log(`${name}: syncing GWP qty ${gwpItem.quantity} → ${desiredQty}`);
          await withRetry(() => setLineQuantity(gwpItem.key, desiredQty));
          didChange = true;
        }

        // Lock the gift line controls
        disableQuantityControls(gwpVariantId);
      }

      // Refresh SlideCart once at end if cart changed
      if (didChange) {
        window.SLIDECART_UPDATE?.();

        // Verify after rerender / async cart reconciliation
        scheduleEnforcement(VERIFY_AFTER_CHANGE_MS, "verify_after_change");
      } else {
        // Still lock controls after rerenders
        for (const rule of GWP_RULES) {
          if (rule?.gwpVariantId) disableQuantityControls(String(rule.gwpVariantId));
        }
      }
    } catch (e) {
      warn("GWP enforcement error", e);
    } finally {
      enforcing = false;

      // Critical: never drop triggers that happen mid-run
      if (pending) {
        pending = false;
        log("Running pending enforcement pass…");
        // Run immediately (no longWait) to catch missed updates
        enforceAllGwpRules({ longWait: false, reason: "pending_rerun" });
      }
    }
  }

  // --------------------
  // Debounced scheduling
  // --------------------
  let enforceTimeout = null;
  function scheduleEnforcement(delay = ENFORCE_DEBOUNCE_MS, reason = "debounced") {
    clearTimeout(enforceTimeout);
    enforceTimeout = setTimeout(() => enforceAllGwpRules({ longWait: false, reason }), delay);
  }

  // --------------------
  // Observer (scoped to SlideCart if present)
  // --------------------
  function getObserverTarget() {
    // Prefer SlideCart root if available; falls back to body.
    return document.querySelector("#slidecarthq") || document.querySelector(".slidecarthq") || document.body;
  }

  let gwpObserver = null;
  function startObserver() {
    if (gwpObserver) return;

    const target = getObserverTarget();
    gwpObserver = new MutationObserver(() => scheduleEnforcement(ENFORCE_DEBOUNCE_MS, "mutation"));
    gwpObserver.observe(target, { childList: true, subtree: true });

    log("GWP observer started", { target: target === document.body ? "body" : "slidecart-root" });
  }

  // If SlideCart root appears later, we can re-scope observer once
  let rescopeDone = false;
  function rescopeObserverIfNeeded() {
    if (rescopeDone) return;
    const slidecartRoot = document.querySelector("#slidecarthq") || document.querySelector(".slidecarthq");
    if (!slidecartRoot) return;

    try {
      gwpObserver?.disconnect?.();
    } catch {}
    gwpObserver = null;

    startObserver();
    rescopeDone = true;
  }

  // --------------------
  // Init
  // --------------------
  function init() {
    startObserver();

    // If SlideCart loads after DOMContentLoaded, re-scope once
    const initObserver = new MutationObserver(() => rescopeObserverIfNeeded());
    initObserver.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(() => {
      try { initObserver.disconnect(); } catch {}
    }, 4000);

    // Initial run (long wait)
    enforceAllGwpRules({ longWait: true, reason: "init" });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();